import { CYLINDER_WRITE_CLOSED_MESSAGE } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useStore } from 'zustand';
import {
  assembleEditAreaInput,
  unsupportedDefinitionNotice,
  useEditAreaDraftStore,
} from '@/entities/edit-area';
import { Button } from '@/shared/ui';
import { useEditAreaSession } from '../model/use-edit-area-session';

export const EditAreaToolbar = (): ReactElement => {
  const store = useEditAreaDraftStore();
  const state = useStore(store);
  const session = useEditAreaSession();
  const validation = assembleEditAreaInput(state);
  const unsupportedNotice = unsupportedDefinitionNotice(state.unsupportedReasons);
  const navigate = (action: 'undo' | 'redo'): void => {
    if (
      (state.dirty || Object.keys(state.operations).length > 0) &&
      !window.confirm('미저장 입력과 사진 작업을 버리고 저장 버전을 이동하시겠습니까?')
    )
      return;
    void session.run(action);
  };
  return (
    <section
      id="edit-area-save"
      tabIndex={-1}
      aria-label="편집영역 저장과 버전"
      className="space-y-3 rounded-lg border p-4"
    >
      <div className="flex flex-wrap items-center gap-3">
        <p>
          {state.serverState
            ? `조회 v${state.serverState.viewing.version} / 최신 v${state.serverState.latestVersion}`
            : '아직 저장된 편집영역이 없습니다.'}
        </p>
        {state.dirty ? (
          <span className="text-amber-700">
            미저장 변경 있음 — [편집영역 저장]을 눌러야 반영됩니다
          </span>
        ) : null}
        <Button
          type="button"
          variant="outline"
          disabled={state.locked || !state.serverState?.canUndo}
          onClick={() => navigate('undo')}
        >
          저장 버전 되돌리기
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={state.locked || !state.serverState?.canRedo}
          onClick={() => navigate('redo')}
        >
          저장 버전 다시 실행
        </Button>
        <Button
          type="button"
          disabled={state.locked || !validation.success || state.isUnsupported}
          onClick={() => void session.run('save')}
        >
          {state.locked ? '처리 중…' : '편집영역 저장'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={state.locked}
          onClick={() => void session.checkServer()}
        >
          서버 상태·사진 URL 확인
        </Button>
      </div>
      {unsupportedNotice !== null ? <p role="alert">{unsupportedNotice}</p> : null}
      {Object.keys(state.pendingInputs).length > 0 ? (
        <p role="status">
          입력 중인 인쇄 영역·표면·배치의 ‘적용’ 버튼을 누른 뒤 전체 편집영역을 저장해 주세요.
        </p>
      ) : null}
      {session.message ? <p role="status">{session.message}</p> : null}
      {session.error ? (
        <div role="alert" className="text-destructive">
          <p>{session.error}</p>
          {/* 게이트 상태를 읽는 API 는 없어 서버 쓰기 게이트(api edit-areas.service)가 닫힌 것은 저장 실패 문구(계약 상수)로만 알 수 있다 — 이 문구일 때만 일반 재시도 안내를 바꾼다(결정 Q12). */}
          {session.error === CYLINDER_WRITE_CLOSED_MESSAGE ? (
            <p>
              서버의 원통 쓰기 게이트가 닫혀 있습니다. 운영 담당자에게 개방을 요청하세요. 입력은
              유지됩니다.
            </p>
          ) : (
            <p>
              입력은 유지됩니다. 실패 원인을 해결하고 다시 시도하세요. 통신 실패로 결과가 불분명하면
              서버 상태를 먼저 확인하세요.
            </p>
          )}
        </div>
      ) : null}
    </section>
  );
};
